import { useState, useRef, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth';
import { isFullWidthPath } from './layoutPaths';

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

function getInitials(name: string | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function getDisplayName(profile: { name?: string; given_name?: string; family_name?: string; email?: string } | undefined): string {
  if (!profile) return 'User';
  if (profile.name) return profile.name;
  if (profile.given_name && profile.family_name) return `${profile.given_name} ${profile.family_name}`;
  if (profile.given_name) return profile.given_name;
  if (profile.email) return profile.email;
  return 'User';
}

const appVersion = import.meta.env.VITE_APP_VERSION || 'local-dev';

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Full-width views without sidebar: dashboard, site entry, site views and site map
  const isFullWidth = isFullWidthPath(location.pathname);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    // Prevent double-clicks
    if (isLoggingOut) return;
    
    setIsLoggingOut(true);
    setIsUserMenuOpen(false);
    
    // Don't await - signoutRedirect will navigate away from the page
    logout().catch((error) => {
      console.error('Logout error:', error);
      setIsLoggingOut(false);
    });
  };

  const displayName = getDisplayName(user?.profile);
  const userInitials = getInitials(user?.profile?.name);

  return (
    <div className="app-layout">
      <header className="app-header">
        <Link to="/" className="app-header__logo-link">
          <img src="/HorizontalAdmin.svg" alt="Toptracer Go Admin" className="app-header__logo" />
        </Link>
        
        <div className="app-header__user" ref={menuRef}>
          <button
            className="app-header__user-button"
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            aria-expanded={isUserMenuOpen}
            aria-haspopup="true"
            disabled={isLoggingOut}
          >
            <span className="app-header__user-icon">{userInitials}</span>
            <span>{displayName}</span>
            <ChevronDownIcon />
          </button>
          
          {isUserMenuOpen && (
            <div className="app-header__user-menu" role="menu">
              <button
                className="app-header__user-menu-item"
                onClick={handleLogout}
                role="menuitem"
                disabled={isLoggingOut}
              >
                {isLoggingOut ? 'Signing out...' : 'Sign out'}
              </button>
            </div>
          )}
        </div>
      </header>

      <div className={`app-container${isFullWidth ? ' app-container--no-sidebar' : ''}`}>
        <main className={`app-main${isFullWidth ? ' app-main--full-width' : ''}`}>
          <Outlet />
        </main>
      </div>

      <footer className="app-footer">
        <span className="app-footer__version">{appVersion}</span>
      </footer>
    </div>
  );
};

export default Layout;
